import { z } from "zod";
import {
  EvalComparisonStatusSchema,
  EvalJudgeKindSchema,
  EvalWinnerSchema,
} from "../../enums/evals";
import {
  EvalJudgmentPassSchema,
  EvalJudgmentPerMetricSchema,
  EvalVariantBriefingSchema,
} from "./judge";

/**
 * Admin comparison surface: `GET /api/evals/comparisons/:comparisonId` and
 * `POST /api/evals/comparisons/:comparisonId/human-judgment`.
 *
 * The detail view reuses the judge's own variant briefing, so an analyst reads
 * exactly the outputs the judge read — with the variant identities attached,
 * which the judge's prompt deliberately withholds.
 */

export const EvalJudgmentSchema = z.object({
  judgeKind: EvalJudgeKindSchema,
  winner: EvalWinnerSchema,
  // Always present on an LLM judgment; a human may record a bare verdict.
  rationale: z.string().nullable(),
  perMetric: z.array(EvalJudgmentPerMetricSchema),
  // Both null on a human judgment: there was no model and no position-swapped
  // pass, and naming one would falsify the audit trail.
  judgeModel: z.string().nullable(),
  passes: z.array(EvalJudgmentPassSchema).nullable(),
  createdAt: z.coerce.date(),
});
export type EvalJudgment = z.infer<typeof EvalJudgmentSchema>;

export const EvalComparisonDetailResponseSchema = z.object({
  comparisonId: z.uuid(),
  batchId: z.uuid(),
  websiteId: z.uuid(),
  url: z.string(),
  status: EvalComparisonStatusSchema,
  runAId: z.uuid().nullable(),
  runBId: z.uuid().nullable(),
  /**
   * Whether the pair actually analysed identical evidence: both runs' capture
   * digests are equal. False on a complete pair means the comparison is not a
   * fair one and its judgment says more about the fetch than the agent. Null
   * while a run still has no capture.
   */
  evidenceMatches: z.boolean().nullable(),
  variants: z.object({
    a: EvalVariantBriefingSchema,
    b: EvalVariantBriefingSchema,
  }),
  judgments: z.array(EvalJudgmentSchema),
});
export type EvalComparisonDetailResponse = z.infer<
  typeof EvalComparisonDetailResponseSchema
>;

export const EvalHumanJudgmentRequestSchema = z
  .object({
    winner: EvalWinnerSchema,
    rationale: z.string().min(1).nullish(),
  })
  .strict();
export type EvalHumanJudgmentRequest = z.infer<
  typeof EvalHumanJudgmentRequestSchema
>;

// The write answers with the id it inserted; the caller reloads the
// comparison rather than trusting a locally assembled judgment.
export const EvalHumanJudgmentResponseSchema = z.object({
  judgmentId: z.uuid(),
});
export type EvalHumanJudgmentResponse = z.infer<
  typeof EvalHumanJudgmentResponseSchema
>;
